import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import * as _ from 'underscore';
import { RootStoreState } from 'src/app/root-store';
import { takeUntil } from 'rxjs/operators';

import { ActivatedRoute, Router } from '@angular/router';
import { updateCustomerAddressError, updateCustomerAddressLoading, updateCustomerAddressSuccess, updateCustomerCompanyError, updateCustomerCompanyLoading, updateCustomerCompanySuccess, updateCustomerError, updateCustomerLoading, updateCustomerSuccess } from 'src/app/root-store/kwikspace-app-store/selectors/customer.selector';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { ModalComponent } from "src/app/core/components/modal/modal.component";
import { StaticDataService } from 'src/app/services/static-data.service';
import { UpdateCustomer, UpdateCustomerAddress, UpdateCustomerCompany } from 'src/app/root-store/kwikspace-app-store/actions/customer.action';
import { AppRoutes } from 'src/app/shared/endpoints.enum';
import { getCurrentUser } from 'src/app/root-store/main-app-store/selectors/auth.selector';
import { TanToastrService } from 'src/app/services/toastr.service';
import { GetCurrentUser } from 'src/app/root-store/main-app-store/actions/auth.actions';
import { getProvinces, getProvincesError, getProvincesLoading } from 'src/app/root-store/kwikspace-app-store/selectors/kwikspace.selector';
import { GetProvinces } from 'src/app/root-store/kwikspace-app-store/actions/kwikspace.action';

@Component({
  selector: 'kwikspace-edit-customer',
  templateUrl: './edit-customer.component.html',
  styleUrls: ['./edit-customer.component.scss']
})
export class EditCustomerComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();

  @ViewChild("editDetailsModal", { static: false })
  editDetailsModal: ModalComponent;

  @ViewChild("editCompanyModal", { static: false })
  editCompanyModal: ModalComponent;

  @ViewChild("editAddressModal", { static: false })
  editAddressModal: ModalComponent;

  updateCustomerLoading$: Observable<any> = this.store.select(updateCustomerLoading);
  updateCustomerError$: Observable<any> = this.store.select(updateCustomerError);

  updateCustomerCompanyLoading$: Observable<any> = this.store.select(updateCustomerCompanyLoading);
  updateCustomerCompanyError$: Observable<any> = this.store.select(updateCustomerCompanyError);

  updateCustomerAddressLoading$: Observable<any> = this.store.select(updateCustomerAddressLoading);
  updateCustomerAddressError$: Observable<any> = this.store.select(updateCustomerAddressError);

  provincesLoading$: Observable<any> = this.store.select(getProvincesLoading);
  provincesError$: Observable<any> = this.store.select(getProvincesError);

  customerForm: FormGroup;
  companyForm: FormGroup;
  addressForm: FormGroup;

  provinces: any[];
  selectedProvince;

  currentUser;
  currentCompany;
  currentAddress;

  showErrorMessage: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<RootStoreState.RootState>,
    private route: ActivatedRoute,
    private router: Router,
    private staticDataService: StaticDataService,
    private toastrService: TanToastrService,
  ) { }

  ngOnInit() {
    this.currentUser = null;
    this.currentCompany = null;
    this.currentAddress = null;

    this.customerForm = this.formBuilder.group({
      first_name: [null, [Validators.required, Validators.maxLength(50)]],
      last_name: [null, [Validators.required, Validators.maxLength(50)]],
      email: [null, [Validators.email]]
    });

    this.companyForm = this.formBuilder.group({
      name: [null, [Validators.required, Validators.maxLength(255)]],
      description: [null, []],
      contact_person: [null, [Validators.required, Validators.maxLength(50)]],
      email: [null, [Validators.email]],
      contact_number: [null, [Validators.required]],
      cell_number: [null, []]
    });

    this.addressForm = this.formBuilder.group({
      address_line1: [null, [Validators.required]],
      address_line2: [null, [Validators.required]],
      suburb: [null, [Validators.required]],
      province: [null, [Validators.required]],
      postcode: [null, [Validators.required]],
    });

    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user) => {
        if (user) {
          this.currentUser = user.customer;
          this.currentCompany = user.customer.company;
          this.currentAddress = user.customer.address;
        }
      });

    this.store
      .select(getProvinces)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((provinces: any) => {
        if (provinces && provinces.results) {
          this.provinces = provinces.results;
        }
      });

    this.store.dispatch(new GetProvinces());
  }

  setProvince() {
    this.selectedProvince = this.addressForm.value.province
  }

  editDetails() {
    this.customerForm.get('first_name').setValue(this.currentUser.firstName);
    this.customerForm.get('last_name').setValue(this.currentUser.lastName);
    this.customerForm.get('email').setValue(this.currentUser.email);
    this.editDetailsModal.open();
  }

  editCompany() {
    this.companyForm.get('name').setValue(this.currentCompany.name);
    this.companyForm.get('description').setValue(this.currentCompany.description);
    this.companyForm.get('contact_person').setValue(this.currentCompany.contactPerson);
    this.companyForm.get('email').setValue(this.currentCompany.email);
    this.companyForm.get('contact_number').setValue(this.currentCompany.contactNumber);
    this.companyForm.get('cell_number').setValue(this.currentCompany.cellNumber);
    this.editCompanyModal.open();
  }

  editAddress() {
    this.addressForm.get('address_line1').setValue(this.currentAddress.addressLine1);
    this.addressForm.get('address_line2').setValue(this.currentAddress.addressLine2);
    this.addressForm.get('suburb').setValue(this.currentAddress.suburb);
    this.addressForm.get('province').setValue(this.currentAddress.province.id);
    this.addressForm.get('postcode').setValue(this.currentAddress.postcode);
    this.editAddressModal.open();
  }

  saveCompany() {
    const payload: any = {
      name: this.companyForm.value.name,
      description: this.companyForm.value.description,
      contact_person: this.companyForm.value.contact_person,
      email: this.companyForm.value.email,
      contact_number: this.companyForm.value.contact_number,
      cell_number: this.companyForm.value.cell_number
    };

    this.store
      .select(updateCustomerCompanySuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((company) => {
        if (company) {
          this.store.dispatch(new GetCurrentUser());

          this.showErrorMessage = false;
          this.toastrService.showSuccessToaster("Success", "Company updated", null);
          this.editCompanyModal.close();
          this.ngOnInit();
        }
      });

    this.store
      .select(updateCustomerCompanyError)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((error) => {
        if (error) {
          this.showErrorMessage = true;
          this.toastrService.showSuccessToaster("Failed", "Could not update Company", null);
        }
      });

    this.store.dispatch(new UpdateCustomerCompany(payload, this.currentCompany.id));
  }

  saveAddress() {
    const payload: any = {
      address_line1: this.addressForm.value.address_line1,
      address_line2: this.addressForm.value.address_line2,
      suburb: this.addressForm.value.suburb,
      province: this.selectedProvince,
      postcode: this.addressForm.value.postcode,
    };

    this.store
      .select(updateCustomerAddressSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((address) => {
        if (address) {
          this.store.dispatch(new GetCurrentUser());

          this.showErrorMessage = false;
          this.toastrService.showSuccessToaster("Success", "Address updated", null);
          this.editAddressModal.close();
          this.ngOnInit();
        }
      });

    this.store
      .select(updateCustomerAddressError)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((error) => {
        if (error) {
          this.showErrorMessage = true;
          this.toastrService.showSuccessToaster("Failed", "Could not update Address", null);
        }
      });

    this.store.dispatch(new UpdateCustomerAddress(payload, this.currentAddress.id));
  }

  saveCustomer() {
    const payload: any = {
      first_name: this.customerForm.value.first_name,
      last_name: this.customerForm.value.last_name,
      email: this.customerForm.value.email,
    };

    this.store
      .select(updateCustomerSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((customer) => {
        if (customer) {
          this.store.dispatch(new GetCurrentUser());

          this.showErrorMessage = false;
          this.toastrService.showSuccessToaster("Success", "Customer updated", null);
          this.editDetailsModal.close();
          this.ngOnInit();
        }
      });

    this.store
      .select(updateCustomerError)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((error) => {
        if (error) {
          this.showErrorMessage = true;
          this.toastrService.showSuccessToaster("Failed", "Could not update Customer", null);
        }
      });

    this.store.dispatch(new UpdateCustomer(payload, this.currentUser.id));

  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

}
